import { cn } from "@/lib/cn";
import { type CSSProperties, type ReactNode, forwardRef } from "react";

export type StatusPillVariant =
  | "ok"
  | "warn"
  | "err"
  /** Informational state — true, not a problem. Added for IR-1548 session
   *  outcomes so "visited, empty" stops sharing `idle` grey with
   *  "not attempted"; the two are different facts. */
  | "info"
  | "down"
  | "idle"
  | "banned"
  | "disconnected"
  // IR-1769 (O-4): an operator Stop, its own status.
  | "stopped"
  | "inactive"
  /** IR-1614: a group the phone archived. Cyan — the kit's blue token, the
   *  same colour `StatusDot` gives its `archived` variant on the Groups tab
   *  (IR-1487). Informational: neither an error (red) nor the attention
   *  state (orange). */
  | "archived"
  /** IR-1753 r8: a session round whose attempt is not known (the matrix's
   *  Unknown). Purple, so it is neither `idle` grey ("not attempted") nor a
   *  fault colour. */
  | "unknown";

export interface StatusPillProps {
  /** Visual state — maps to iris-status--{variant} CSS class */
  variant?: StatusPillVariant;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

/**
 * Pill-shaped status label using the iris-status CSS classes from kit.css.
 * Renders labels like "OK", "WARN", "FAILED" with neon coloring.
 */
export const StatusPill = forwardRef<HTMLSpanElement, StatusPillProps>(
  ({ variant = "ok", children, className, style }, ref) => {
    return (
      <span
        ref={ref}
        className={cn("iris-status", `iris-status--${variant}`, className)}
        style={style}
      >
        {children}
      </span>
    );
  },
);
StatusPill.displayName = "StatusPill";
